/**
 * Document 2 · Phase 4.4c — BillingOverridesPanel
 *
 * Admin-only UI to grant comped/extended access without going through Stripe.
 * Two columns:
 *   - INDIVIDUAL OVERRIDES → users.access_override*
 *   - COMPANY PLANS         → company_accounts collection
 *
 * The resolver in services/billing.py already reads BOTH sources, so granting
 * here unlocks the user immediately.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import {
  Shield, Building2, UserPlus, Trash2, Loader2, Plus, Calendar, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';


const TIER_LABEL = { starter: 'Starter', professional: 'Professional', elite: 'Elite' };


function fmtDate(iso) {
  if (!iso) return 'Permanent';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}


export default function BillingOverridesPanel() {
  const api = useApi();
  const [data, setData] = useState({ users: [], companies: [], audit: [] });
  const [loading, setLoading] = useState(true);
  const [grantUserOpen, setGrantUserOpen] = useState(false);
  const [grantCompanyOpen, setGrantCompanyOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/billing/admin/overrides');
      setData(res || { users: [], companies: [], audit: [] });
    } catch (e) {
      toast.error(e?.message || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const revokeUser = async (uid, email) => {
    if (!window.confirm(`Revoke override for ${email || uid}? They'll fall back to their normal subscription / trial / lapsed state.`)) return;
    try {
      await api.delete(`/billing/admin/overrides/user/${uid}`);
      toast.success('Override revoked');
      load();
    } catch (e) {
      toast.error(e?.message || 'Revoke failed');
    }
  };

  const revokeCompany = async (cid, name) => {
    if (!window.confirm(`Revoke company plan "${name}"? All ${data.companies.find((c) => c.company_id === cid)?.user_ids?.length || 0} member(s) will lose company-granted access.`)) return;
    try {
      await api.delete(`/billing/admin/overrides/company/${cid}`);
      toast.success('Company plan revoked');
      load();
    } catch (e) {
      toast.error(e?.message || 'Revoke failed');
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="billing-overrides-panel">
      <CardContent className="p-5 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-sm p-2 bg-[#B89B5E]/10 text-[#B89B5E]">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                Manual Overrides
              </div>
              <div className="font-serif text-2xl md:text-3xl leading-none">
                Comped & company access
              </div>
              <div className="mt-2 text-xs text-muted-foreground max-w-md">
                Grant tier access without Stripe — for partners, beta users, internal staff,
                or company-wide plans. These take priority over individual subscriptions.
              </div>
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={load}
            className="rounded-sm" data-testid="overrides-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* INDIVIDUAL */}
        <section data-testid="overrides-users-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Individual users
              <Badge variant="outline" className="rounded-sm text-[10px]">{data.users.length}</Badge>
            </h3>
            <Button
              size="sm" onClick={() => setGrantUserOpen(true)}
              className="rounded-sm h-8" data-testid="overrides-grant-user-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Grant access
            </Button>
          </div>

          {loading ? (
            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
          ) : data.users.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed rounded-sm p-4 text-center">
              No individual overrides active. Click "Grant access" to comp a user.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="overrides-users-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Tier</th>
                    <th className="py-2 pr-3">Expires</th>
                    <th className="py-2 pr-3">Granted by</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id} className="border-b last:border-b-0">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium">{u.name || u.email}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{u.email}</div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge className="rounded-sm" style={{ backgroundColor: '#B89B5E', color: 'white' }}>
                          {TIER_LABEL[u.access_override_tier] || u.access_override_tier}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-3 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        {fmtDate(u.access_override_expiry)}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                        {u.access_override_granted_by || '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-xs">
                        {u.access_override_reason || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2.5 text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => revokeUser(u.id, u.email)}
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`overrides-revoke-user-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* COMPANIES */}
        <section data-testid="overrides-companies-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Company plans
              <Badge variant="outline" className="rounded-sm text-[10px]">{data.companies.length}</Badge>
            </h3>
            <Button
              size="sm" onClick={() => setGrantCompanyOpen(true)}
              className="rounded-sm h-8" data-testid="overrides-grant-company-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New company plan
            </Button>
          </div>

          {loading ? (
            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
          ) : data.companies.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed rounded-sm p-4 text-center">
              No company plans. Use this for multi-seat agreements outside Stripe.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="overrides-companies-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">Tier</th>
                    <th className="py-2 pr-3">Members</th>
                    <th className="py-2 pr-3">Expires</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((c) => (
                    <tr key={c.company_id} className="border-b last:border-b-0">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium">{c.company_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{c.company_id}</div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Badge className="rounded-sm" style={{ backgroundColor: '#B89B5E', color: 'white' }}>
                          {TIER_LABEL[c.tier] || c.tier}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-3 text-xs">
                        {(c.user_ids || []).length}
                      </td>
                      <td className="py-2.5 pr-3 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        {fmtDate(c.expiry)}
                      </td>
                      <td className="py-2.5 text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => revokeCompany(c.company_id, c.company_name)}
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`overrides-revoke-company-${c.company_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Grant USER dialog */}
        <GrantUserDialog
          open={grantUserOpen}
          onOpenChange={setGrantUserOpen}
          onGranted={load}
        />

        {/* Grant COMPANY dialog */}
        <GrantCompanyDialog
          open={grantCompanyOpen}
          onOpenChange={setGrantCompanyOpen}
          onGranted={load}
        />
      </CardContent>
    </Card>
  );
}


// ─────────── grant user dialog ───────────

function GrantUserDialog({ open, onOpenChange, onGranted }) {
  const api = useApi();
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('professional');
  const [duration, setDuration] = useState('365');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail(''); setTier('professional'); setDuration('365'); setReason('');
  };

  const submit = async () => {
    if (!email.trim()) {
      toast.error('Enter the user email');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        user_email: email.trim().toLowerCase(),
        tier,
        reason: reason.trim() || null,
      };
      const days = parseInt(duration, 10);
      if (Number.isFinite(days) && days > 0) body.duration_days = days;
      await api.post('/billing/admin/overrides/user', body);
      toast.success(`${tier.toUpperCase()} access granted to ${email}`);
      reset();
      onOpenChange(false);
      onGranted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e?.message || 'Grant failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="overrides-grant-user-dialog">
        <DialogHeader>
          <DialogTitle>Grant individual access</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">User email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="rounded-sm"
              data-testid="overrides-grant-user-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger className="rounded-sm" data-testid="overrides-grant-user-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Duration (days)</Label>
              <Input
                type="number"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="0 = permanent"
                className="rounded-sm"
                data-testid="overrides-grant-user-duration"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Leave blank or 0 for permanent.
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Beta partner, internal QA, replacement for failed payment, etc."
              className="rounded-sm"
              data-testid="overrides-grant-user-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-sm" data-testid="overrides-grant-user-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Grant access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────── grant company dialog ───────────

function GrantCompanyDialog({ open, onOpenChange, onGranted }) {
  const api = useApi();
  const [companyName, setCompanyName] = useState('');
  const [tier, setTier] = useState('professional');
  const [duration, setDuration] = useState('365');
  const [userIds, setUserIds] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCompanyName(''); setTier('professional'); setDuration('365');
    setUserIds(''); setReason('');
  };

  const submit = async () => {
    if (!companyName.trim()) {
      toast.error('Enter a company name');
      return;
    }
    setSubmitting(true);
    try {
      const ids = userIds
        .split(/[,\s\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        company_name: companyName.trim(),
        tier,
        user_ids: ids,
        reason: reason.trim() || null,
      };
      const days = parseInt(duration, 10);
      if (Number.isFinite(days) && days > 0) body.duration_days = days;
      await api.post('/billing/admin/overrides/company', body);
      toast.success(`Company plan created for ${companyName}`);
      reset();
      onOpenChange(false);
      onGranted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e?.message || 'Failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="overrides-grant-company-dialog">
        <DialogHeader>
          <DialogTitle>Create company plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Company name</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Corp"
              className="rounded-sm"
              data-testid="overrides-grant-company-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger className="rounded-sm" data-testid="overrides-grant-company-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Duration (days)</Label>
              <Input
                type="number"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="rounded-sm"
                data-testid="overrides-grant-company-duration"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Member user IDs</Label>
            <Textarea
              rows={2}
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              placeholder="user_id_1, user_id_2, ..."
              className="rounded-sm font-mono text-xs"
              data-testid="overrides-grant-company-user-ids"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Comma- or newline-separated. You can edit this list later.
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-sm"
              data-testid="overrides-grant-company-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-sm" data-testid="overrides-grant-company-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Create plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
